import React, { useCallback, useEffect, useMemo } from 'react';
import { useProducts } from './useProducts';
import HomeDynamicCatalog from './HomeDynamicCatalog';
import HomeNextExhibitionBanner from './HomeNextExhibitionBanner';
import { useExhibitions } from './useExhibitions';
import { useSiteSettings } from './SiteSettingsContext';
import PageSeoHelmet from './PageSeoHelmet';
import StorefrontHero from './components/StorefrontHero';
import HomeBlogSection from './components/home/HomeBlogSection';
import HomeInstagramSection from './components/home/HomeInstagramSection';
import { mergeBlogContent } from './content/siteCopyHelpers';
import RecentlyViewedStrip from './recentlyViewed/RecentlyViewedStrip';
import { usePullToRefresh } from './usePullToRefresh';

const Home = () => {
  const { allProducts, refetch: refetchCatalog } = useProducts();
  const exhibitions = useExhibitions();
  const { settings: site, refresh: refreshSettings } = useSiteSettings();

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      refetchCatalog({ silent: true }),
      refreshSettings(),
      exhibitions.refetch(),
    ]);
  }, [exhibitions.refetch, refetchCatalog, refreshSettings]);

  const { pulling, ready, refreshing, pullDistance, threshold } = usePullToRefresh(handleRefresh);

  const blogContent = useMemo(() => mergeBlogContent(site.blog_content), [site.blog_content]);
  const homeCopy = site.page_copy?.home || {};

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="Home-main">
      <PageSeoHelmet page="home" />

      {(pulling || refreshing) ? (
        <div
          className={`home-pull-refresh${ready ? ' home-pull-refresh--ready' : ''}${refreshing ? ' home-pull-refresh--active' : ''}`}
          aria-live="polite"
          aria-hidden={!pulling && !refreshing}
          style={{ '--pull-progress': `${Math.min(100, (pullDistance / threshold) * 100)}%` }}
        >
          <span className="home-pull-refresh__label">
            {refreshing ? 'Refreshing…' : ready ? 'Release to refresh' : 'Pull to refresh'}
          </span>
        </div>
      ) : null}

      <div className="home-top-strips">
        {exhibitions.status === 'ready' && exhibitions.upcoming.length > 0 && (
          <HomeNextExhibitionBanner upcoming={exhibitions.upcoming} homeCopy={homeCopy} />
        )}
      </div>

      <StorefrontHero />

      <HomeDynamicCatalog />

      <RecentlyViewedStrip className="recently-viewed home-recently-viewed" />

      <HomeInstagramSection site={site} homeCopy={homeCopy} allProducts={allProducts} />

      <HomeBlogSection blogContent={blogContent} homeCopy={homeCopy} />
    </div>
  );
};

export default Home;
